import { ChartContainer } from 'components/ui/chart';
import { FormattedMessage, useIntl } from 'react-intl';
import { Pie, PieChart } from 'recharts';
import { formatPercent } from './figureFormat';
import { Fitted, Headline, KpiFormProps, ShareBar, targetShare } from './KpiParts';

/** The ring of the two-row size: 128px across and 12px thick, and in the same proportion where it is drawn smaller. */
const RING = { size: 128, innerRadius: '81.25%' };

/**
 * A figure against its target: a bar in one row, a ring in two. Value, target, share, and what is left. In two rows
 * the ring stands beside its text while the value fits there in full; a value that does not takes the card's whole
 * width under a smaller ring, and is written short only if that is too narrow as well.
 */
export function ProgressForm({ definition, figure, size, links, format, capped }: KpiFormProps) {
    const intl = useIntl();
    const target = definition.target ?? 0;
    const share = targetShare(figure.value, target);
    const percent = formatPercent(share);
    const left = target - figure.value;

    const value = (
        <Headline value={figure.value} format={format} to={links?.list} capped={capped} roomier={size !== '1x1'} />
    );
    const ofTarget = (short?: boolean) =>
        intl.formatMessage({ id: 'home.widgets.target.of-short' }, { target: format(target, short) });
    // What is left of a count that stopped at its list's cap is not known.
    const toGo =
        left > 0 && !capped ? (
            <FormattedMessage id="home.widgets.target.left" values={{ value: format(left) }} />
        ) : null;

    if (size === '1x1') {
        return (
            <>
                <div className="flex min-h-7 min-w-0 flex-1 items-baseline gap-1.5">
                    {value}
                    {/* It gives way to the value, down to 80px beside one in full and to nothing beside a short
                        one, and is asked how it fits beside the value in full: hence again when that changes. */}
                    <Fitted
                        key={figure.value}
                        full={ofTarget()}
                        short={ofTarget(true)}
                        className="text-meta text-muted-foreground min-w-20 shrink peer-data-[short]:min-w-0"
                    />
                </div>
                <ShareBar share={share} trackClassName="bg-neutral-tint h-2" />
                <span className="text-muted-foreground text-caption mt-0.5 truncate">
                    <strong className="text-foreground font-semibold">{percent}</strong>
                    {toGo ? <> · {toGo}</> : null}
                </span>
            </>
        );
    }

    const done = Math.min(Math.max(share, 0), 1);
    return (
        <div className="group/progress flex min-h-0 flex-1 items-center gap-5 has-[[data-cramped]]:flex-col has-[[data-cramped]]:items-start has-[[data-cramped]]:justify-center has-[[data-cramped]]:gap-2">
            <ChartContainer
                config={{}}
                initialDimension={{ width: RING.size, height: RING.size }}
                role="img"
                aria-label={intl.formatMessage({ id: 'home.widgets.target.of' }, { percent, target: format(target) })}
                className="aspect-square size-32 shrink-0 group-has-[[data-cramped]]/progress:size-24"
            >
                {/* No margin, so that the ring's radii are shares of the whole box. */}
                <PieChart accessibilityLayer={false} margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
                    {/* Starts at 12 o'clock and runs clockwise. */}
                    <Pie
                        data={[
                            { value: done, fill: 'var(--color-primary)' },
                            { value: 1 - done, fill: 'var(--color-neutral-tint)' },
                        ]}
                        dataKey="value"
                        innerRadius={RING.innerRadius}
                        outerRadius="100%"
                        startAngle={90}
                        endAngle={-270}
                        stroke="none"
                        isAnimationActive={false}
                        rootTabIndex={-1}
                    />
                    <text
                        x="50%"
                        y="50%"
                        dy="0.35em"
                        textAnchor="middle"
                        className="fill-foreground text-figure font-sans group-has-[[data-cramped]]/progress:text-xl"
                    >
                        {percent}
                    </text>
                </PieChart>
            </ChartContainer>
            <div className="flex min-w-0 flex-1 flex-col gap-0.5 group-has-[[data-cramped]]/progress:w-full group-has-[[data-cramped]]/progress:flex-none">
                {value}
                <span className="text-meta text-muted-foreground">{ofTarget()}</span>
                {toGo ? <span className="text-meta text-foreground-secondary mt-2">{toGo}</span> : null}
            </div>
        </div>
    );
}
